import { AccountingPeriodStatus } from '@cpce/database/enums';
import {
  type AccountingPeriodClosureChecks,
  type AccountingPeriodWithRelations,
  CLOSED_PERIOD_ERROR,
  isDateInPeriods,
} from '@cpce/shared';
import { useMemo } from 'react';
import { commiteeQuery } from '@/api/queries.utils';
import { accountingPeriodsQueryKeys } from './accounting-periods.query-keys';

export function useAccountingPeriods() {
  return commiteeQuery<AccountingPeriodWithRelations[]>({
    queryKey: accountingPeriodsQueryKeys.all,
    url: '/commitee/:commiteeId/accounting-periods',
  });
}

export function useIsDateInClosedPeriod(): (date: Date | string) => boolean {
  const { data: periods } = useAccountingPeriods();
  return useMemo(() => {
    const closedPeriods = periods?.filter((p) => p.status === AccountingPeriodStatus.closed) ?? [];
    return (date) => isDateInPeriods(closedPeriods, date);
  }, [periods]);
}

/** The server's refusal to rewrite an écriture dated `date`, when its exercice is closed for good. */
export function useClosedPeriodRefusal(date: Date | string | undefined): string | null {
  const isDateInClosedPeriod = useIsDateInClosedPeriod();
  return date !== undefined && isDateInClosedPeriod(date) ? CLOSED_PERIOD_ERROR : null;
}

export function useAccountingPeriod(id: number) {
  return commiteeQuery<AccountingPeriodWithRelations>({
    queryKey: accountingPeriodsQueryKeys.one(id),
    url: `/commitee/:commiteeId/accounting-periods/${id}`,
  });
}

export function useAccountingPeriodClosureChecks(id: number) {
  return commiteeQuery<AccountingPeriodClosureChecks>({
    queryKey: accountingPeriodsQueryKeys.closureChecks(id),
    url: `/commitee/:commiteeId/accounting-periods/${id}/closure-checks`,
  });
}
